// ContentAwareG35Gate — the Quality-Uplift Q7 G3.5 Coherence Gate
// review surface (plan ``AI_ALM_Quality_Uplift_Implementation_Plan.md``
// §7.1 step 4).
//
// G3.5 sits between Phase 3 and Phase 4 and surfaces two classes of
// finding the deterministic post-Phase-3 pipeline persisted but
// couldn't safely auto-resolve:
//
//   * Q7 citation findings whose ``kind`` is ``unknown_section`` /
//     ``unknown_anchor`` / ``format_unparseable`` / ``miscitation`` —
//     the architect picks (A) accept the verifier's suggestion, (B)
//     keep the original (intentional), or (C) edit by hand.
//   * Q5 dedup ``needs_review`` rows — duplicates referenced by
//     downstream tests / wireframes that the deduplicator declined to
//     silently delete. Three options: (A) delete anyway, (B) keep
//     both, or (C) merge by hand at G4.
//
// Every option-card click writes the option_id into the decision's
// ``answer`` field and flips ``status`` to ``approved`` (or
// ``edited`` for manual_edit / merge_manual). Submit posts the full
// decision list to the backend resolver (see
// ``ai_alm.services.g35_wiring.apply_g35_submission``), which
// applies citation rewrites + dedup deletions to ``brd.md`` /
// ``srd.md`` atomically and persists ``g35-resolutions.json`` for
// the audit-package replay.
//
// Volume-management toolbar (added 2026-05-05 in response to the
// drone-management UAT — 37 cards rendered as a flat list with no
// grouping, no search, and no progress signal):
//
//   * **Search** — substring filter against requirement id /
//     original citation / detail / statement.
//   * **Group by citation** — collapses every card sharing the same
//     ``original_citation`` into one expandable group with a single
//     "Apply to all" bulk button. Most coherence runs only have a
//     handful of distinct broken citations spread across dozens of
//     requirements; grouping turns 30 cards into 4 groups.
//   * **Hide decided** — once an architect picks an option, fold the
//     card so they can focus on what's still pending.
//   * **Progress chip** — live "n / total decided" so the architect
//     knows when they're done without scrolling.
//   * **Pagination** — when the visible list still exceeds 25 rows
//     after filtering, page through them so the DOM stays cheap.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Edit3,
  EyeOff,
  Info,
  Layers,
  Loader2,
  PlayCircle,
  RefreshCw,
  Search,
  ShieldAlert,
  X,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord, Project } from '@/types'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'

// G3.5 is stored numerically as 35 in the API; the user-facing label
// is "G3.5" everywhere. Mirrors the backend's ``gate_label`` helper.
const G35_GATE_NUMBER = 35

// Option ids mirror :data:`ai_alm.orchestrator.gate_emitter.G35_OPTION_*`
// — keep these strings in lock-step with the backend constants.
//
// v0.7 dedup option layout (UAT 2026-05-05): the ``Delete`` option
// is now ``delete_and_rewire`` (auto-rewires downstream references
// so we never leave a hole), and ``merge_manual`` is replaced by
// three deterministic merge variants — keep canonical wording,
// use the duplicate's wording, or supply replacement text. None
// require a manual rewrite at G4. The legacy ``delete_anyway`` /
// ``merge_manual`` ids are still accepted by the backend resolver
// (as aliases) but we don't render them as options here.
const OPT_ACCEPT_SUGGESTED = 'accept_suggested'
const OPT_KEEP_ORIGINAL = 'keep_original'
const OPT_MANUAL_EDIT = 'manual_edit'
const OPT_DELETE_ANYWAY = 'delete_anyway' // legacy alias — not rendered
const OPT_DELETE_AND_REWIRE = 'delete_and_rewire'
const OPT_KEEP_BOTH = 'keep_both'
const OPT_MERGE_MANUAL = 'merge_manual' // legacy alias — not rendered
const OPT_MERGE_KEEP_CANONICAL = 'merge_keep_canonical'
const OPT_MERGE_USE_DUPLICATE = 'merge_use_duplicate'
const OPT_MERGE_REPLACE_TEXT = 'merge_replace_text'


// Decision-id prefixes the backend emits for the two finding classes.
const PREFIX_CITATION = 'G35-CIT-'
const PREFIX_DEDUP = 'G35-DUP-'

// Volume controls — when the visible list grows past this threshold
// the panel paginates so we don't render hundreds of cards into the
// DOM at once. The number is tuned for laptop-sized viewports; the
// architect always sees the toolbar + a usable handful of cards.
const PAGE_SIZE = 25
// Above this many *visible* citation cards we default the gate to
// group-by-citation mode so the architect lands on a manageable
// list. They can still flip to flat view via the toolbar.
const AUTO_GROUP_THRESHOLD = 12

interface G35Option {
  id: string
  label: string
  summary: string
  destructive?: boolean
  needs_text?: boolean
  // Wave A2 (2026-05-19) — the gate emitter now marks ``keep_both``
  // as recommended on dedup decisions so a high-volume G3.5 (the
  // robot-navigation case had 25 cards) doesn't nudge architects
  // toward bulk-deletion. The badge is rendered next to the option
  // label; the option's selection behaviour is unchanged.
  recommended?: boolean
}

interface CitationPayload {
  kind: 'citation'
  finding_kind?: string
  requirement_id?: string
  original_citation?: string
  suggested_citation?: string | null
  suggested_section?: string | null
  suggestion_source?: string | null
  detail?: string
  statement?: string | null
  // ``anchor_section`` / ``anchor_title`` describe the closest
  // existing charter section (e.g. "3" → "Goal Hierarchy") so the
  // card can answer "what was Section 3.1 supposed to be?" without
  // the architect leaving the panel.
  anchor_section?: string | null
  anchor_title?: string | null
  // Top-level outline of the charter ([{section, title}]) — last-
  // resort context when there's no anchor match (e.g. the citation
  // was for a totally bogus section).
  charter_outline?: Array<{ section: string; title: string }> | null
  score?: number | null
  options: G35Option[]
}

interface DedupPayload {
  kind: 'dedup'
  duplicate_id?: string
  canonical_id?: string
  method?: string
  score?: number | null
  reason?: string
  // Both statements are pulled from BRD/SRD by the loader so the
  // architect can compare side-by-side before picking
  // delete/keep/merge.
  duplicate_statement?: string | null
  canonical_statement?: string | null
  // Source citations the duplicate carried with it — useful when
  // deciding whether merging would lose a regulatory anchor.
  merged_citations?: string[] | null
  options: G35Option[]
}

type DecisionPayload = CitationPayload | DedupPayload

export interface ContentAwareG35Props {
  project: Project
  runId: number
  /** Called after the gate is approved so the page can advance focus. */
  onApproved?: () => void
}

export function ContentAwareG35Gate({
  project,
  runId,
  onApproved,
}: ContentAwareG35Props) {
  const queryClient = useQueryClient()
  const draftKey = gateDraftKey(project.id, runId, G35_GATE_NUMBER)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)

  // Per-decision free-text input for ``manual_edit`` / ``merge_manual``.
  // Keyed by decision id; lives outside the draft because the answer
  // field is reused for the option_id (so the resolver knows which
  // path was taken). A separate buffer keeps the architect's text
  // intact while they pick options back and forth.
  const [manualText, setManualText] = useState<Record<string, string>>({})

  // ----- Toolbar / volume-management state ------------------------- //
  const [searchTerm, setSearchTerm] = useState('')
  const [hideDecided, setHideDecided] = useState(false)
  // ``groupMode`` is auto-decided once the gate loads but the user
  // can flip it. ``null`` means "not yet seeded from data".
  const [groupMode, setGroupMode] = useState<boolean | null>(null)
  // Per-group collapse state so expanding one group doesn't dump
  // every other group's cards into the DOM at once.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  )
  const [page, setPage] = useState(0)

  // Wave A3 (2026-05-19) — Phase-3 quality summary inputs. We render
  // a top-of-panel card showing how many BR/SR the swarm emitted and
  // how many survived the dedup pipeline, so the architect doesn't
  // bulk-approve destructive merges without realising the collapse
  // ratio. Fetched directly from disk (the gate JSON itself doesn't
  // carry these numbers).
  const [dedupAudit, setDedupAudit] = useState<{
    clusters_found?: number
    auto_merged_byte_identical?: number
    auto_merged_cosine_above_95?: number
    needs_review_cosine_band?: number
    final_state?: {
      human_decisions_required_at_g35?: number
    }
  } | null>(null)
  const [lintReport, setLintReport] = useState<{
    entries?: Array<{
      agent_name?: string
      valid?: boolean
      violations?: unknown[]
      repaired_attempts?: number
    }>
  } | null>(null)

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, G35_GATE_NUMBER],
    queryFn: () => api.getGate(project.id, runId, G35_GATE_NUMBER),
    retry: false,
  })

  // Seed the draft from the server payload, reconciling any stale
  // localStorage draft against the server's decision-id set first.
  // ``reconcileDraftWithGate`` keeps real in-flight edits intact
  // (subset match) and discards drafts whose ids no longer line up
  // with the freshly emitted gate (deleted-and-recreated project,
  // re-emit that changed the finding set, etc).
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  // Auto-pick group mode based on citation volume on first load.
  useEffect(() => {
    if (groupMode !== null) return
    if (decisions.length === 0) return
    const citationCount = decisions.filter((d) =>
      d.id.startsWith(PREFIX_CITATION),
    ).length
    setGroupMode(citationCount >= AUTO_GROUP_THRESHOLD)
  }, [decisions, groupMode])

  // Wave A3 (2026-05-19) — fetch phase3/dedup-audit.json +
  // phase3/lint-report.json so the summary card can show the
  // emit-vs-survived collapse ratio. Best-effort: 404 yields silent
  // null + no card.
  useEffect(() => {
    let cancelled = false
    async function load() {
      const runFolder = `runs/run-${String(runId).padStart(3, '0')}`
      try {
        const res = await fetch(
          projectFileUrl(project.id, `${runFolder}/phase3/dedup-audit.json`),
          { cache: 'no-store' },
        )
        if (res.ok && !cancelled) setDedupAudit(await res.json())
      } catch {
        /* ignore */
      }
      try {
        const res = await fetch(
          projectFileUrl(project.id, `${runFolder}/phase3/lint-report.json`),
          { cache: 'no-store' },
        )
        if (res.ok && !cancelled) setLintReport(await res.json())
      } catch {
        /* ignore */
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [project.id, runId])

  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, G35_GATE_NUMBER),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      gate.refetch()
      toast({ variant: 'success', title: 'G3.5 re-emitted' })
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Re-emit failed',
        description: (err as Error).message,
      }),
  })

  const submit = useMutation({
    mutationFn: () =>
      api.submitGateDecisions(
        project.id,
        runId,
        G35_GATE_NUMBER,
        decisions,
      ),
    onSuccess: (g) => {
      gate.refetch()
      // Invalidate downstream artefact queries so the audit-diff +
      // post-G3.5 BRD/SRD are re-fetched on the next focus.
      queryClient.invalidateQueries({
        queryKey: ['artifact-index', project.id, runId],
      })
      if (g.status === 'approved') {
        toast({
          variant: 'success',
          title: 'G3.5 approved',
          description: 'Coherence findings resolved — ready to run Phase 4.',
        })
        onApproved?.()
      } else if (g.status === 'modified') {
        toast({
          variant: 'success',
          title: 'G3.5 resolved with edits',
          description:
            'Manual edits recorded. Inspect the resolution log under phase3/g35-resolutions.json.',
        })
        onApproved?.()
      }
    },
    onError: (err) => {
      // The resolver returns 422 with the offending decision ids when
      // any decision is pending or unknown. Surface those inline so
      // the architect can see what's missing.
      const message = (err as Error).message
      toast({
        variant: 'destructive',
        title: 'Could not submit decisions',
        description: message,
      })
    },
  })

  const updateDecision = (id: string, patch: Partial<GateDecisionRecord>) =>
    setDecisions(decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)))

  const updateMany = (
    ids: string[],
    patch: Partial<GateDecisionRecord>,
  ) => {
    const idSet = new Set(ids)
    setDecisions(
      decisions.map((d) => (idSet.has(d.id) ? { ...d, ...patch } : d)),
    )
  }

  const allDecided =
    decisions.length > 0 && decisions.every((d) => d.status !== 'pending')

  // Counts for the header chips.
  const counts = useMemo(() => {
    const citation = decisions.filter((d) => d.id.startsWith(PREFIX_CITATION))
    const dedup = decisions.filter((d) => d.id.startsWith(PREFIX_DEDUP))
    const decided = decisions.filter((d) => d.status !== 'pending')
    return {
      citation: citation.length,
      dedup: dedup.length,
      total: decisions.length,
      decided: decided.length,
    }
  }, [decisions])

  // ----- Filtering / grouping derivation -------------------------- //
  const filteredDecisions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return decisions.filter((d) => {
      if (hideDecided && d.status !== 'pending') return false
      if (!term) return true
      const payload = d.payload as DecisionPayload | undefined
      const haystacks: string[] = [d.id, d.question]
      if (payload?.kind === 'citation') {
        haystacks.push(
          payload.requirement_id ?? '',
          payload.original_citation ?? '',
          payload.detail ?? '',
          payload.statement ?? '',
          payload.suggested_citation ?? '',
        )
      } else if (payload?.kind === 'dedup') {
        haystacks.push(
          payload.duplicate_id ?? '',
          payload.canonical_id ?? '',
          payload.reason ?? '',
        )
      }
      return haystacks.join(' ').toLowerCase().includes(term)
    })
  }, [decisions, hideDecided, searchTerm])

  // Group citations sharing the same ``original_citation`` so the
  // architect can "Keep all" / "Apply suggested to all" in bulk. Dedup
  // rows + non-grouped citations land in their own per-row buckets so
  // the rendering loop stays uniform.
  type Group = {
    key: string
    label: string
    detail?: string
    suggested?: string | null
    suggested_section?: string | null
    suggestion_source?: string | null
    anchor_section?: string | null
    anchor_title?: string | null
    charter_outline?: Array<{ section: string; title: string }> | null
    decisions: GateDecisionRecord[]
    kind: 'citation_group' | 'single'
  }
  const groups = useMemo<Group[]>(() => {
    if (!groupMode) {
      return filteredDecisions.map((d) => ({
        key: d.id,
        label: d.id,
        decisions: [d],
        kind: 'single',
      }))
    }
    const buckets = new Map<string, Group>()
    const out: Group[] = []
    for (const d of filteredDecisions) {
      const payload = d.payload as DecisionPayload | undefined
      if (payload?.kind === 'citation') {
        const cit = payload as CitationPayload
        const key = `cit::${cit.original_citation ?? ''}::${cit.detail ?? ''}`
        let group = buckets.get(key)
        if (!group) {
          group = {
            key,
            label: cit.original_citation || 'Untitled citation',
            detail: cit.detail,
            suggested: cit.suggested_citation,
            suggested_section: cit.suggested_section,
            suggestion_source: cit.suggestion_source,
            anchor_section: cit.anchor_section,
            anchor_title: cit.anchor_title,
            charter_outline: cit.charter_outline,
            decisions: [],
            kind: 'citation_group',
          }
          buckets.set(key, group)
          out.push(group)
        }
        group.decisions.push(d)
      } else {
        // Dedup + clean confirm-and-advance rows render solo.
        out.push({
          key: d.id,
          label: d.id,
          decisions: [d],
          kind: 'single',
        })
      }
    }
    return out
  }, [filteredDecisions, groupMode])


  // Pagination: applied to the *group* list so a single big group
  // never gets chopped in half. Most groups are tiny (1-3 cards) so
  // the page count is dominated by the number of groups anyway.
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const paginatedGroups = groups.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE,
  )

  // Reset page when filters change so the architect doesn't get
  // stranded on page 4 of a now-1-page list.
  useEffect(() => {
    setPage(0)
  }, [searchTerm, hideDecided, groupMode])

  const handleOptionClick = (
    decision: GateDecisionRecord,
    option: G35Option,
  ) => {
    if (option.needs_text) {
      // For free-text options the architect must type a replacement /
      // note before the gate accepts approval. We encode both the
      // option id AND the typed text as a two-line string so the
      // backend resolver
      // (`ai_alm.services.g35_wiring._parse_dedup_option`) can
      // unambiguously dispatch on the option id while still applying
      // the architect's text. Format:
      //
      //   <option_id>\n<typed text>
      //
      // When the buffer is empty we send the bare option id and the
      // resolver surfaces a "no replacement text supplied" diagnostic
      // on the resolution log so the architect knows to retry.
      const buffered = (manualText[decision.id] ?? '').trim()
      const answer = buffered ? `${option.id}\n${buffered}` : option.id
      updateDecision(decision.id, {
        status: 'edited',
        answer,
      })
      return
    }
    // Single-click options write the id into ``answer`` so the
    // resolver knows which path to take. Status maps onto ``approved``
    // for every non-destructive choice; the destructive
    // ``delete_and_rewire`` option also lands as ``approved`` because
    // the architect has explicitly confirmed it, but the option's
    // own ``destructive: true`` flag drives the red affordance on
    // the card.
    updateDecision(decision.id, {
      status: 'approved',
      answer: option.id,
    })
  }


  // Bulk-apply for citation groups: walk every decision in the group
  // and copy the picked option onto each. Manual-edit is intentionally
  // disabled for bulk because each card needs its own replacement
  // text — surfaced as a disabled state with a tooltip below.
  const handleGroupBulkApply = (
    group: Group,
    optionId: string,
  ) => {
    const ids = group.decisions.map((d) => d.id)
    updateMany(ids, { status: 'approved', answer: optionId })
    toast({
      variant: 'success',
      title: `Applied to ${ids.length} finding${ids.length === 1 ? '' : 's'}`,
      description: optionLabel(optionId),
    })
  }

  const missing = gate.error instanceof ApiError && gate.error.status === 404
  const notVisible =
    gate.error instanceof ApiError && gate.error.code === 'gate.not_visible'

  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn">
              <AlertCircle className="h-3 w-3" /> Needs you
            </span>
            <span className="alm-chip">Gate G3.5</span>
            {counts.citation > 0 && (
              <span className="alm-chip alm-chip-info">
                {counts.citation} citation
                {counts.citation === 1 ? '' : 's'}
              </span>
            )}
            {counts.dedup > 0 && (
              <span className="alm-chip alm-chip-info">
                {counts.dedup} dedup
                {counts.dedup === 1 ? '' : 's'}
              </span>
            )}
            {counts.total > 0 && (
              <span
                className={[
                  'alm-chip',
                  counts.decided === counts.total
                    ? 'alm-chip-info'
                    : 'alm-chip-warn',
                ].join(' ')}
              >
                {counts.decided} / {counts.total} decided
              </span>
            )}
            {gate.data?.status && (
              <span className="alm-chip">{gate.data.status}</span>
            )}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Coherence Gate
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Q5 dedup + Q7 citation verifier ran clean-up passes on the
            BRD / SRD. The findings below need your judgement before the
            Critic runs.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => emit.mutate()}
            disabled={emit.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50"
          >
            {emit.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}{' '}
            Re-emit
          </button>
        </div>
      </div>

      {notVisible && (
        <div className="rounded-md border border-amber-700/60 bg-amber-950/20 p-4 mb-5 text-sm text-amber-200/80">
          <p className="font-semibold">Gate skipped</p>
          <p className="mt-1">
            G3.5 isn't visible at the current intervention budget. Switch
            to Standard or Thorough to surface citation + dedup
            findings.
          </p>
        </div>
      )}

      {missing && !notVisible && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-5 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Click <strong>Re-emit</strong> above to build the G3.5
            artifact from <code>citation-report.json</code> and{' '}
            <code>dedup-ledger.json</code>.
          </p>
        </div>
      )}

      {/* Wave A3 (2026-05-19) — Phase-3 quality summary. Surfaces the
          dedup collapse ratio + prevention-linter status BEFORE the
          architect starts clicking through 25 G3.5 cards. Source
          files (dedup-audit.json + lint-report.json) are loaded
          lazily; the card only renders when at least one is
          available. */}
      {(dedupAudit || lintReport) && !missing && !notVisible && (
        <Phase3QualitySummaryCard
          dedupAudit={dedupAudit}
          lintReport={lintReport}
        />
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {!missing && !notVisible && (
        <>
          {/* Why-this-gate explainer (mirrors G3 onboarding hygiene). */}
          <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mb-5 flex gap-2 text-xs text-slate-400">
            <Info className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <strong className="text-slate-200">
                Why this gate exists:
              </strong>{' '}
              after Phase 3 the deterministic pipeline auto-fixed every
              citation it could (e.g. <code>Charter Section 3 Outc</code>{' '}
              → <code>Charter Section 3 Outcome A1</code>) and collapsed
              every byte-identical duplicate. What's left needs your
              call: a citation that points to a missing section, a
              requirement that's a near-miss for a different chapter, a
              duplicate that downstream tests reference. Each finding
              ships with three explicit options — no row leaves G3.5
              pending.
            </div>
          </div>

          {/* Volume-management toolbar — only render when the gate
              has more than a handful of decisions (otherwise it's
              just visual noise above two cards). */}
          {decisions.length > 4 && (
            <div className="flex flex-wrap items-center gap-2 mb-3 rounded-lg border border-slate-700/60 bg-slate-950/40 p-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search id, citation, requirement text…"
                  className="w-full bg-slate-900/40 border border-slate-700 rounded-md pl-7 pr-2 py-1 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
                />
              </div>
              <button
                type="button"
                onClick={() => setGroupMode((m) => !m)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                  groupMode
                    ? 'border-indigo-500/60 bg-indigo-950/30 text-indigo-200'
                    : 'border-slate-700 text-slate-300 hover:bg-white/5',
                ].join(' ')}
                title="Group identical citations into one row with bulk-apply"
              >
                <Layers className="h-3 w-3" />{' '}
                {groupMode ? 'Grouped by citation' : 'Group by citation'}
              </button>
              <button
                type="button"
                onClick={() => setHideDecided((v) => !v)}
                className={[
                  'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                  hideDecided
                    ? 'border-emerald-500/60 bg-emerald-950/30 text-emerald-200'
                    : 'border-slate-700 text-slate-300 hover:bg-white/5',
                ].join(' ')}
                title="Hide cards you've already decided so you can focus on what's left"
              >
                <EyeOff className="h-3 w-3" />{' '}
                {hideDecided ? 'Hiding decided' : 'Hide decided'}
              </button>
              {groups.length === 0 && (
                <span className="text-[11px] text-slate-500 italic">
                  No matches for the current filters.
                </span>
              )}
            </div>
          )}

          {/* Decisions */}
          <div className="space-y-3 mb-6">
            {paginatedGroups.map((group) => {
              if (group.kind === 'single') {
                const decision = group.decisions[0]
                const payload =
                  (decision.payload as DecisionPayload | undefined) ?? null
                if (!payload || !payload.options) {
                  return (
                    <div key={decision.id} className="alm-card">
                      <div
                        className="flex items-start justify-between gap-3"
                        data-testid="gate-35-row"
                      >
                        <div className="min-w-0 text-sm text-slate-200">
                          <div className="text-[10.5px] text-slate-500">
                            {decision.id}
                          </div>
                          <div className="mt-0.5">{decision.question}</div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            type="button"
                            data-testid="gate-35-approve"
                            onClick={() =>
                              updateDecision(decision.id, {
                                status: 'approved',
                                answer: 'confirmed',
                              })
                            }
                            className="alm-decision"
                            data-selected={
                              decision.status === 'approved'
                                ? 'approved'
                                : undefined
                            }
                          >
                            <Check className="h-3 w-3" /> Confirm
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <FindingCard
                    key={decision.id}
                    decision={decision}
                    payload={payload}
                    manualText={manualText[decision.id] ?? ''}
                    setManualText={(value) =>
                      setManualText((m) => ({ ...m, [decision.id]: value }))
                    }
                    onOptionClick={(opt) => handleOptionClick(decision, opt)}
                  />
                )
              }
              // Citation group — render the shared header + per-row
              // children below an expand toggle.
              const expanded = expandedGroups[group.key] ?? false
              const decided = group.decisions.filter(
                (d) => d.status !== 'pending',
              ).length
              const total = group.decisions.length
              return (
                <CitationGroupCard
                  key={group.key}
                  group={group}
                  expanded={expanded}
                  decided={decided}
                  total={total}
                  onToggle={() =>
                    setExpandedGroups((m) => ({
                      ...m,
                      [group.key]: !expanded,
                    }))
                  }
                  onBulkApply={(optionId) =>
                    handleGroupBulkApply(group, optionId)
                  }
                  manualText={manualText}
                  setManualText={(id, value) =>
                    setManualText((m) => ({ ...m, [id]: value }))
                  }
                  onOptionClick={handleOptionClick}
                />
              )
            })}
          </div>

          {/* Pagination footer — only renders when needed. */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mb-5 text-xs text-slate-400">
              <div>
                Showing{' '}
                <span className="text-slate-200 font-mono">
                  {safePage * PAGE_SIZE + 1}
                </span>
                –
                <span className="text-slate-200 font-mono">
                  {Math.min((safePage + 1) * PAGE_SIZE, groups.length)}
                </span>{' '}
                of{' '}
                <span className="text-slate-200 font-mono">
                  {groups.length}
                </span>{' '}
                {groupMode ? 'group' : 'finding'}
                {groups.length === 1 ? '' : 's'}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage(0)}
                  disabled={safePage === 0}
                  className="rounded-md border border-slate-700 px-1.5 py-1 hover:bg-white/5 disabled:opacity-40"
                >
                  <ChevronsLeft className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="rounded-md border border-slate-700 px-1.5 py-1 hover:bg-white/5 disabled:opacity-40"
                >
                  <ChevronRight className="h-3 w-3 rotate-180" />
                </button>
                <span className="px-2 font-mono text-slate-200">
                  {safePage + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={safePage >= totalPages - 1}
                  className="rounded-md border border-slate-700 px-1.5 py-1 hover:bg-white/5 disabled:opacity-40"
                >
                  <ChevronRight className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage(totalPages - 1)}
                  disabled={safePage >= totalPages - 1}
                  className="rounded-md border border-slate-700 px-1.5 py-1 hover:bg-white/5 disabled:opacity-40"
                >
                  <ChevronsRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {gate.data?.status === 'approved' && (
            <div className="rounded-lg p-4 mb-5 border border-emerald-700/40 bg-emerald-950/20 flex items-center gap-2 text-emerald-300 text-sm">
              <CheckCircle2 className="h-4 w-4" /> G3.5 approved — run
              Phase 4 to re-audit
            </div>
          )}

          {/* Primary actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="gate-35-submit"
              onClick={() => submit.mutate()}
              disabled={!allDecided || submit.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
              title="Apply citation rewrites + dedup deletions and approve the gate"
            >
              {submit.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Applying…
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4" /> Apply &amp; continue
                </>
              )}
            </button>
            {!allDecided && (
              <span className="text-[11px] text-slate-500 italic">
                Pick an option for every finding above to enable Apply (
                {counts.total - counts.decided} pending).
              </span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// --------------------------------------------------------------------- //
// CitationGroupCard — renders the shared header for a group of
// citations sharing the same broken ``original_citation``. Bulk
// "Apply to all" buttons execute the picked option against every
// decision in the group. Individual rows live below the expand
// toggle so the architect can override one-off if they need to.
// --------------------------------------------------------------------- //
function CitationGroupCard({
  group,
  expanded,
  decided,
  total,
  onToggle,
  onBulkApply,
  manualText,
  setManualText,
  onOptionClick,
}: {
  group: {
    key: string
    label: string
    detail?: string
    suggested?: string | null
    suggested_section?: string | null
    suggestion_source?: string | null
    anchor_section?: string | null
    anchor_title?: string | null
    charter_outline?: Array<{ section: string; title: string }> | null
    decisions: GateDecisionRecord[]
  }
  expanded: boolean
  decided: number
  total: number
  onToggle: () => void
  onBulkApply: (optionId: string) => void
  manualText: Record<string, string>
  setManualText: (id: string, value: string) => void
  onOptionClick: (
    decision: GateDecisionRecord,
    option: G35Option,
  ) => void
}) {
  const hasSuggestion = Boolean(group.suggested || group.suggested_section)
  const isParentFallback = group.suggestion_source === 'parent_section_fallback'
  // Pull a sample requirement statement off the first decision so the
  // collapsed group header gives the architect at least one piece of
  // concrete context (e.g. "BR-003 — flag DCMA Blue UAS …") without
  // forcing them to expand. Plus the list of sibling ids so they
  // can see the surface area at a glance.
  const samplePayload = group.decisions[0]?.payload as
    | CitationPayload
    | undefined
  const sampleStatement = samplePayload?.statement ?? ''
  const requirementIds = group.decisions
    .map((d) => (d.payload as CitationPayload | undefined)?.requirement_id)
    .filter((id): id is string => Boolean(id))
  const idsPreview =
    requirementIds.length <= 6
      ? requirementIds.join(', ')
      : `${requirementIds.slice(0, 6).join(', ')} (+ ${
          requirementIds.length - 6
        } more)`
  return (
    <div className="alm-card overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="alm-chip alm-chip-info">citation group</span>
            <span className="alm-chip">{total} requirement{total === 1 ? '' : 's'}</span>
            <span
              className={[
                'alm-chip',
                decided === total ? 'alm-chip-info' : 'alm-chip-warn',
              ].join(' ')}
            >
              {decided} / {total} decided
            </span>
            {hasSuggestion && isParentFallback && (
              <span className="alm-chip alm-chip-warn" title="Deterministic fallback (parent section). No LLM ranking was run.">
                fallback
              </span>
            )}
          </div>
          <div className="text-sm font-semibold text-slate-100">
            <span className="text-slate-400 font-normal">cited as:</span>{' '}
            <code>{group.label}</code>
          </div>
          {group.detail && (
            <div className="text-xs text-slate-400 mt-1 leading-relaxed">
              {group.detail}
            </div>
          )}
          {/* Affects: which requirement ids share this citation. */}
          {requirementIds.length > 0 && (
            <div className="text-[11px] text-slate-500 mt-1.5">
              <span className="uppercase tracking-wide text-slate-600 mr-1">
                Affects:
              </span>
              <span className="font-mono text-slate-300">{idsPreview}</span>
            </div>
          )}
          {/* What was the missing section *supposed* to be? */}
          {group.anchor_section && group.anchor_title && (
            <div className="text-[11px] text-slate-400 mt-1">
              <span className="uppercase tracking-wide text-slate-600 mr-1">
                Closest existing section:
              </span>
              <span className="text-slate-200">
                §{group.anchor_section} · {group.anchor_title}
              </span>
            </div>
          )}
          {/* First sample statement — gives the architect concrete
              context without expanding the group. */}
          {sampleStatement && (
            <div className="text-[11.5px] text-slate-300 mt-2 leading-relaxed border-l-2 border-slate-700/80 pl-2.5 italic">
              <span className="not-italic text-slate-500 text-[10px] uppercase tracking-wide mr-1">
                Sample requirement:
              </span>
              {sampleStatement}
            </div>
          )}
          {!hasSuggestion && (
            <div className="text-[11px] text-amber-300/70 mt-2 italic">
              No automatic suggestion available — keep originals or edit each by hand.
            </div>
          )}
          {/* Charter outline as last-resort context — only show when
              we have NO better anchor (so the architect at least
              knows what sections do exist when they have to edit by
              hand). */}
          {!group.anchor_title &&
            group.charter_outline &&
            group.charter_outline.length > 0 && (
              <details className="mt-2 text-[11px] text-slate-500">
                <summary className="cursor-pointer hover:text-slate-300">
                  Charter outline ({group.charter_outline.length}{' '}
                  top-level sections)
                </summary>
                <ul className="mt-1 ml-3 space-y-0.5">
                  {group.charter_outline.map((s) => (
                    <li key={s.section}>
                      <span className="font-mono text-slate-400 mr-1">
                        §{s.section}
                      </span>
                      <span className="text-slate-300">{s.title}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-white/5 shrink-0"
        >
          {expanded ? (
            <>
              <ChevronDown className="h-3 w-3" /> Collapse
            </>
          ) : (
            <>
              <ChevronRight className="h-3 w-3" /> Show {total} card{total === 1 ? '' : 's'}
            </>
          )}
        </button>
      </div>

      {/* Bulk-apply row — visible on the collapsed group so the
          architect can blast through identical citations without
          expanding. */}
      <div className="mt-3 pt-3 border-t border-slate-700/40 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">
          Apply to all {total}:
        </span>
        {hasSuggestion && (
          <button
            type="button"
            onClick={() => onBulkApply(OPT_ACCEPT_SUGGESTED)}
            className="inline-flex items-center gap-1 rounded-md border border-indigo-500/40 bg-indigo-950/20 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-900/30"
            title={
              isParentFallback
                ? 'Apply the deterministic parent-section fallback to every requirement in this group'
                : 'Apply the suggested citation to every requirement in this group'
            }
          >
            <ArrowRight className="h-3 w-3" /> Suggested
            {group.suggested ? ` → ${group.suggested}` : ''}
          </button>
        )}
        <button
          type="button"
          onClick={() => onBulkApply(OPT_KEEP_ORIGINAL)}
          className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-white/5"
          title="Confirm every citation in this group is intentional"
        >
          <Check className="h-3 w-3" /> Keep original
        </button>
        <span
          className="text-[10.5px] text-slate-500 italic"
          title="Manual edits need per-card replacement text — open the group and edit each card."
        >
          Manual edit per-card only
        </span>
      </div>

      {/* Expanded per-row cards. We render lightweight rows here
          (just id + statement + chosen-option pill) so 30+ items
          stay scannable. The architect can still override the bulk
          choice per row via the option buttons. */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-700/40 space-y-2">
          {group.decisions.map((decision) => {
            const payload = decision.payload as CitationPayload | undefined
            return (
              <div
                key={decision.id}
                className="rounded-md border border-slate-800/80 bg-slate-950/40 p-2.5"
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-[10.5px] text-indigo-400">
                    {payload?.requirement_id ?? decision.id}
                  </span>
                  <DecisionStatusPill decision={decision} />
                </div>
                {payload?.statement && (
                  <div className="text-[11.5px] text-slate-300 leading-relaxed mb-1.5">
                    {payload.statement}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {payload?.options.map((opt) => {
                    const selected = decision.answer === opt.id
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => onOptionClick(decision, opt)}
                        className={[
                          'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px]',
                          selected
                            ? opt.destructive
                              ? 'border-red-500/60 bg-red-950/30 text-red-200'
                              : 'border-indigo-500/60 bg-indigo-950/30 text-indigo-200'
                            : 'border-slate-700 text-slate-300 hover:bg-white/5',
                        ].join(' ')}
                      >
                        <OptionIcon
                          optionId={opt.id}
                          selected={selected}
                          small
                        />
                        {shortOptionLabel(opt)}
                      </button>
                    )
                  })}
                </div>
                {decision.answer === OPT_MANUAL_EDIT && (
                  <textarea
                    value={manualText[decision.id] ?? ''}
                    onChange={(e) => {
                      setManualText(decision.id, e.target.value)
                      onOptionClick(decision, {
                        id: OPT_MANUAL_EDIT,
                        label: 'Edit citation by hand',
                        summary: '',
                        needs_text: true,
                      })
                    }}
                    rows={1}
                    placeholder="Paste the corrected citation"
                    className="mt-1.5 w-full bg-slate-950/60 border border-amber-700/50 rounded-md px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                  />
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --------------------------------------------------------------------- //
// FindingCard — renders a single citation / dedup decision with its
// three option cards. Selected option pulses; the manual-edit / merge-
// manual variants gain an inline textarea below the cards. Used both
// for dedup rows and for citation rows when group-by-citation is OFF.
// --------------------------------------------------------------------- //
function FindingCard({
  decision,
  payload,
  manualText,
  setManualText,
  onOptionClick,
}: {
  decision: GateDecisionRecord
  payload: DecisionPayload
  manualText: string
  setManualText: (v: string) => void
  onOptionClick: (option: G35Option) => void
}) {
  const isCitation = payload.kind === 'citation'
  const headerChip = isCitation ? 'citation' : 'dedup'
  const findingKind = isCitation
    ? (payload as CitationPayload).finding_kind ?? 'finding'
    : (payload as DedupPayload).method ?? 'duplicate'
  const targetId = isCitation
    ? (payload as CitationPayload).requirement_id ?? '?'
    : `${(payload as DedupPayload).duplicate_id ?? '?'} → ${
        (payload as DedupPayload).canonical_id ?? '?'
      }`
  const detail = isCitation
    ? (payload as CitationPayload).detail ?? ''
    : (payload as DedupPayload).reason ?? ''
  const statement = isCitation
    ? (payload as CitationPayload).statement ?? ''
    : ''
  const suggestionSource = isCitation
    ? (payload as CitationPayload).suggestion_source ?? null
    : null
  const isParentFallback = suggestionSource === 'parent_section_fallback'

  const selectedOptionId = decision.answer ?? null
  const selectedOption = payload.options.find(
    (o) => o.id === selectedOptionId,
  )
  const needsManualText = Boolean(
    selectedOption && selectedOption.needs_text,
  )

  // Dedup-specific context: pull the duplicate + canonical
  // statements off the payload so we can render them side-by-side.
  // The backend loader (`g35_wiring._extract_requirement_statements`)
  // populates them from BRD/SRD; legacy ledgers won't have them, in
  // which case we gracefully fall back to id-only labels and the
  // user can still decide based on the reason text.
  const dedupPayload = !isCitation ? (payload as DedupPayload) : null
  const duplicateStatement = dedupPayload?.duplicate_statement ?? ''
  const canonicalStatement = dedupPayload?.canonical_statement ?? ''
  const mergedCitations = dedupPayload?.merged_citations ?? []
  const dedupScore =
    dedupPayload?.score !== undefined && dedupPayload?.score !== null
      ? Math.round((dedupPayload.score as number) * 100)
      : null
  // Citation-specific anchor context for the flat (non-grouped) view.
  const citPayload = isCitation ? (payload as CitationPayload) : null
  const anchorSection = citPayload?.anchor_section ?? ''
  const anchorTitle = citPayload?.anchor_title ?? ''

  return (
    <div className="alm-card overflow-hidden" data-testid="gate-35-row">
      <div className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-slate-700/50">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-[10.5px] text-indigo-400">
              {decision.id}
            </span>
            <span className="alm-chip alm-chip-info">{headerChip}</span>
            <span className="alm-chip">{findingKind}</span>
            <span className="text-[10.5px] font-semibold text-slate-400">
              → {targetId}
            </span>
            {isCitation &&
              (payload as CitationPayload).original_citation && (
                <span className="alm-chip">
                  cited as: ``
                  {(payload as CitationPayload).original_citation}
                  ``
                </span>
              )}
            {dedupScore !== null && (
              <span
                className="alm-chip"
                title="Cosine similarity between the two requirements"
              >
                {dedupScore}% similar
              </span>
            )}
            {isParentFallback && (
              <span
                className="alm-chip alm-chip-warn"
                title="Deterministic fallback to the closest existing parent section. No LLM ranking was performed."
              >
                fallback
              </span>
            )}
          </div>
          {/* Citation: render the requirement statement +
              section-title context. */}
          {isCitation && statement && (
            <div className="text-[12.5px] text-slate-200 mt-1.5 leading-relaxed border-l-2 border-slate-700/80 pl-2.5 italic">
              {statement}
            </div>
          )}
          {isCitation && anchorSection && anchorTitle && (
            <div className="text-[11px] text-slate-400 mt-1.5">
              <span className="uppercase tracking-wide text-slate-600 mr-1">
                Closest existing section:
              </span>
              <span className="text-slate-200">
                §{anchorSection} · {anchorTitle}
              </span>
            </div>
          )}
          {detail && (
            <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">
              {detail}
            </div>
          )}
          {/* Dedup: render the duplicate vs canonical side-by-side
              so the architect can compare them on one card. The
              audit feedback was "G35-DUP-SR-001 speaks of a
              duplicate, what is this duplicate referring to" —
              both statements answer that directly. */}
          {!isCitation && (duplicateStatement || canonicalStatement) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              <div className="rounded-md border border-amber-700/40 bg-amber-950/10 p-2.5">
                <div className="text-[9.5px] uppercase tracking-wide text-amber-300/70 font-semibold mb-1">
                  Duplicate (proposed for deletion):{' '}
                  <span className="font-mono">
                    {dedupPayload?.duplicate_id}
                  </span>
                </div>
                <div className="text-[12px] text-slate-200 leading-relaxed">
                  {duplicateStatement || (
                    <span className="italic text-slate-500">
                      (statement not found in brd.md / srd.md)
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-md border border-emerald-700/40 bg-emerald-950/10 p-2.5">
                <div className="text-[9.5px] uppercase tracking-wide text-emerald-300/70 font-semibold mb-1">
                  Canonical (kept):{' '}
                  <span className="font-mono">
                    {dedupPayload?.canonical_id}
                  </span>
                </div>
                <div className="text-[12px] text-slate-200 leading-relaxed">
                  {canonicalStatement || (
                    <span className="italic text-slate-500">
                      (statement not found in brd.md / srd.md)
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          {!isCitation && mergedCitations.length > 0 && (
            <div className="text-[11px] text-slate-500 mt-2">
              <span className="uppercase tracking-wide text-slate-600 mr-1">
                Citations carried by the duplicate:
              </span>
              {mergedCitations.map((c, i) => (
                <span key={i} className="font-mono text-slate-300 mr-2">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Option cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        {payload.options.map((opt) => {
          const selected = selectedOptionId === opt.id
          return (
            <button
              key={opt.id}
              type="button"
              data-testid={`gate-35-option-${opt.id}`}
              onClick={() => onOptionClick(opt)}
              className={[
                'text-left rounded-md border px-3 py-2.5 transition',
                selected
                  ? opt.destructive
                    ? 'border-red-500/70 bg-red-950/30 ring-1 ring-red-500/50'
                    : 'border-indigo-500/70 bg-indigo-950/30 ring-1 ring-indigo-500/50'
                  : 'border-slate-700/60 bg-slate-900/40 hover:border-slate-600 hover:bg-slate-900/60',
              ].join(' ')}
            >
              <div className="flex items-start gap-2">
                <OptionIcon optionId={opt.id} selected={selected} />
                <div className="min-w-0 flex-1">
                  <div
                    className={[
                      'text-[12px] font-semibold leading-tight flex items-center gap-1.5 flex-wrap',
                      selected
                        ? opt.destructive
                          ? 'text-red-200'
                          : 'text-indigo-200'
                        : 'text-slate-200',
                    ].join(' ')}
                  >
                    <span>{opt.label}</span>
                    {opt.recommended && (
                      <span
                        className="inline-flex items-center rounded border border-emerald-700/40 bg-emerald-950/30 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-emerald-300"
                        title="Recommended for one-click bulk-approve flows — safest default."
                      >
                        recommended
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                    {opt.summary}
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Inline editor for manual_edit / merge_manual options. */}
      {needsManualText && (
        <div className="mt-2">
          <div className="text-[10px] uppercase font-bold text-amber-500/60 mb-1">
            Your replacement text
          </div>
          <textarea
            data-testid="gate-35-edit-text"
            value={manualText}
            onChange={(e) => {
              setManualText(e.target.value)
              // Mirror the buffered text into the decision answer so
              // the resolver applies it on submit. Falls back to the
              // option id when the architect hasn't typed anything
              // yet, which the resolver flags as "no replacement
              // text supplied" so we never silently keep the
              // original.
              onOptionClick({ ...selectedOption!, needs_text: true })
            }}
            rows={2}
            placeholder={
              isCitation
                ? 'Paste the corrected citation, e.g. ``Charter Section 3 Outcome A1``'
                : 'Add a note describing how you will merge the two requirements at G4'
            }
            className="w-full bg-slate-950/40 border border-amber-700/50 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
          />
        </div>
      )}
    </div>
  )
}

function OptionIcon({
  optionId,
  selected,
  small,
}: {
  optionId: string
  selected: boolean
  small?: boolean
}) {
  const sizeClass = small
    ? 'h-3 w-3 shrink-0'
    : 'h-3.5 w-3.5 shrink-0 mt-0.5'
  const colorClass = selected ? 'text-indigo-300' : 'text-slate-500'
  const className = `${sizeClass} ${colorClass}`
  switch (optionId) {
    case OPT_ACCEPT_SUGGESTED:
      return <ArrowRight className={className} />
    case OPT_KEEP_ORIGINAL:
    case OPT_KEEP_BOTH:
      return <Check className={className} />
    // The three v0.7 merge variants share the edit icon — they all
    // mutate the canonical's wording in some way (verbatim,
    // duplicate's, or architect-supplied).
    case OPT_MANUAL_EDIT:
    case OPT_MERGE_MANUAL:
    case OPT_MERGE_KEEP_CANONICAL:
    case OPT_MERGE_USE_DUPLICATE:
    case OPT_MERGE_REPLACE_TEXT:
      return <Edit3 className={className} />
    case OPT_DELETE_ANYWAY:
    case OPT_DELETE_AND_REWIRE:
      return (
        <ShieldAlert
          className={`${sizeClass} ${
            selected ? 'text-red-300' : 'text-slate-500'
          }`}
        />
      )
    default:
      return <X className={className} />
  }
}


function DecisionStatusPill({
  decision,
}: {
  decision: GateDecisionRecord
}) {
  if (decision.status === 'pending') {
    return (
      <span className="text-[9.5px] uppercase tracking-wide text-amber-400/70">
        pending
      </span>
    )
  }
  return (
    <span className="text-[9.5px] uppercase tracking-wide text-emerald-400/80">
      {optionLabel(decision.answer ?? '') || decision.status}
    </span>
  )
}

function optionLabel(optionId: string): string {
  // Lowercase short labels used by the status pill on each card +
  // the bulk-apply toast. Mirrors the v0.7 option layout so the new
  // delete/merge variants render readable status copy instead of the
  // raw option id.
  switch (optionId) {
    case OPT_ACCEPT_SUGGESTED:
      return 'apply suggested'
    case OPT_KEEP_ORIGINAL:
      return 'keep original'
    case OPT_MANUAL_EDIT:
      return 'manual edit'
    case OPT_DELETE_ANYWAY:
      return 'delete anyway'
    case OPT_DELETE_AND_REWIRE:
      return 'delete + rewire refs'
    case OPT_KEEP_BOTH:
      return 'keep both'
    case OPT_MERGE_MANUAL:
      return 'merge manual'
    case OPT_MERGE_KEEP_CANONICAL:
      return 'merge — keep canonical'
    case OPT_MERGE_USE_DUPLICATE:
      return 'merge — use duplicate'
    case OPT_MERGE_REPLACE_TEXT:
      return 'merge — replacement text'
    default:
      return ''
  }
}

function shortOptionLabel(opt: G35Option): string {
  // Short labels for the per-row buttons inside an expanded group.
  // We can't reuse ``opt.label`` because that includes the literal
  // citation text (``Apply suggested citation: ``Charter Section 3```)
  // which already lives in the group header — duplicating it here
  // makes the group rows feel heavy.
  switch (opt.id) {
    case OPT_ACCEPT_SUGGESTED:
      return 'Apply suggested'
    case OPT_KEEP_ORIGINAL:
      return 'Keep original'
    case OPT_MANUAL_EDIT:
      return 'Edit by hand'
    case OPT_DELETE_ANYWAY:
      return 'Delete anyway'
    case OPT_DELETE_AND_REWIRE:
      return 'Delete + rewire'
    case OPT_KEEP_BOTH:
      return 'Keep both'
    case OPT_MERGE_MANUAL:
      return 'Merge by hand'
    case OPT_MERGE_KEEP_CANONICAL:
      return 'Merge — keep canonical'
    case OPT_MERGE_USE_DUPLICATE:
      return 'Merge — use duplicate'
    case OPT_MERGE_REPLACE_TEXT:
      return 'Merge — your text'
    default:
      return opt.label
  }
}


// --------------------------------------------------------------------------- #
// Wave A3 (2026-05-19) — Phase-3 quality summary card.
//
// Surfaces the dedup collapse ratio and prevention-linter status at the
// TOP of G3.5 so the architect understands what's about to be approved
// before they click through 25 cards. The robot-navigation retrospective
// showed bulk-approve of `delete_and_rewire` silently collapsed 6
// distinct BRs into BR-001 — this card makes the size of that bulk
// action visible up-front.
//
// Data sources (both read directly from disk):
//   * phase3/dedup-audit.json — clusters_found, auto_merged_byte_identical,
//     auto_merged_cosine_above_95, needs_review_cosine_band
//   * phase3/lint-report.json — per-agent prevention-linter results
//
// Either may 404 (e.g. dedup didn't run, prevention disabled); the
// card only renders the sections it has data for.
// --------------------------------------------------------------------------- #
function Phase3QualitySummaryCard({
  dedupAudit,
  lintReport,
}: {
  dedupAudit: {
    clusters_found?: number
    auto_merged_byte_identical?: number
    auto_merged_cosine_above_95?: number
    needs_review_cosine_band?: number
    final_state?: {
      human_decisions_required_at_g35?: number
    }
  } | null
  lintReport: {
    entries?: Array<{
      agent_name?: string
      valid?: boolean
      violations?: unknown[]
      repaired_attempts?: number
    }>
  } | null
}) {
  // Dedup math
  const clusters = dedupAudit?.clusters_found ?? 0
  const autoByte = dedupAudit?.auto_merged_byte_identical ?? 0
  const autoCosine = dedupAudit?.auto_merged_cosine_above_95 ?? 0
  const needsReview = dedupAudit?.needs_review_cosine_band ?? 0
  const autoTotal = autoByte + autoCosine
  const hasDedup = clusters > 0 || autoTotal > 0 || needsReview > 0

  // Prevention math
  const entries = lintReport?.entries ?? []
  const failedAgents = entries.filter((e) => e.valid === false)
  const hasPrevention = entries.length > 0

  if (!hasDedup && !hasPrevention) return null

  return (
    <div
      className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 mb-4"
      data-testid="g35-phase3-quality-summary"
    >
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2">
        Phase-3 quality summary
      </div>

      {hasDedup && (
        <div className="rounded border border-slate-700/40 bg-slate-950/40 px-3 py-2 text-[11.5px] text-slate-300 mb-2">
          <div className="font-medium text-slate-200 mb-1">Dedup pipeline</div>
          <ul className="list-none space-y-0.5 ml-2">
            <li>
              <span className="font-mono text-slate-200">{clusters}</span>{' '}
              duplicate cluster{clusters === 1 ? '' : 's'} detected across
              the BRD + SRD.
            </li>
            {autoTotal > 0 && (
              <li>
                <span className="font-mono text-amber-300">{autoTotal}</span>{' '}
                auto-merged silently
                {autoByte > 0 && autoCosine > 0
                  ? ` (${autoByte} byte-identical, ${autoCosine} cosine ≥ 0.95)`
                  : autoByte > 0
                    ? ' (byte-identical)'
                    : autoCosine > 0
                      ? ' (cosine ≥ 0.95)'
                      : ''}
                .
              </li>
            )}
            {needsReview > 0 && (
              <li>
                <span className="font-mono text-amber-300">
                  {needsReview}
                </span>{' '}
                in the cosine ≥ 0.92 review band — surfaced as cards
                below.{' '}
                <span className="italic text-slate-400">
                  Each one is a destructive merge if you pick anything
                  other than "Keep both".
                </span>
              </li>
            )}
          </ul>
        </div>
      )}

      {hasPrevention && (
        <div
          className={`rounded border px-3 py-2 text-[11.5px] ${
            failedAgents.length === 0
              ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-200/90'
              : 'border-amber-700/40 bg-amber-950/20 text-amber-200/90'
          }`}
        >
          <div className="font-medium mb-1">
            Phase-3 prevention linter
          </div>
          {failedAgents.length === 0 ? (
            <div>
              All <span className="font-mono">{entries.length}</span>{' '}
              lintable agent{entries.length === 1 ? '' : 's'} cleared
              the prevention catalog on the first attempt. No G4
              ``PREV-*`` decisions expected.
            </div>
          ) : (
            <div>
              <span className="font-mono">{failedAgents.length}</span>{' '}
              of <span className="font-mono">{entries.length}</span>{' '}
              agent{entries.length === 1 ? '' : 's'} hit the 3-attempt
              repair cap with unresolved violations:{' '}
              <span className="font-mono">
                {failedAgents.map((e) => e.agent_name).join(', ')}
              </span>
              . These surface as{' '}
              <span className="font-mono">PREV-*</span> decisions on
              G4.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

